import { observer } from 'mobx-react-lite'
import { StyleSheet, View, Image, Platform } from 'react-native'
import { useRouter } from 'expo-router'

import { GAME_CONFIG } from '@ppwr/poker'
import { useActualHeight } from '../../../hooks/use-actual-height'
import { translate } from '../../../i18n/translate'
import { useStore } from '../../../models'
import { events } from '../../../models/analytics/analytics-events'
import { color, fontFamily } from '../../../theme'
import { PlayerCellVideo } from '../PlayerCell/PlayerCellVideo'
import ChatIcon from '../Shortcuts/assets/WhiteChat.svg'
import MicIconOff from '../assets/Mic--off.svg'
import { PlayerActions } from './PlayerActions'
import { PlayerHandLastAction } from './PlayerHandLastAction'
import { PlayerInfo } from './PlayerInfo'
import { PlayerHandWalkthrough } from './Walkthrough/PlayerHandWalkthrough'
import { DS } from '../../../design-system'
import { InfoRectangle } from '../InfoRectangle'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { useEffect, useRef } from 'react'
import { GameActions } from '../../../screens/home-screen/GameActions'
import ColoredStars from '../../../assets/ColoredStars.png'
import { TimerShowCards } from '../BottomModalContent/Content/ShowCards/TimerShowCards'
import { GameModeTag } from '../../../models/api-store'
import { SkeletonLoader } from '../../../screens/home-screen/SkeletonLoader'
import { usePlayerActions, useShortcuts } from '../../../screens/new-design/hooks'
import { useGameBottomModal } from '../../../screens/new-design/hooks/useGameBottomModals'
const isWeb = Platform.OS === 'web'

const GamePaused = observer(() => {
  const {
    game: { isMyPlayerTeacher }
  } = useStore()

  return (
    <View style={[styles.secondRow, { flexDirection: 'column' }]}>
      <View style={[styles.playerLastActionContainer, { alignItems: 'center' }]}>
        <GameAudioOrVideo />

        <DS.Frame flex={1}>
          <InfoRectangle>
            <DS.Text variant="Caption/Bold" uppercase style={{ width: '100%', textAlign: 'center' }}>
              GAME PAUSED
            </DS.Text>
          </InfoRectangle>
        </DS.Frame>
      </View>
      {!isMyPlayerTeacher && <DS.Icon name="PauseGame" size={48} color="Utility/Warning" />}
    </View>
  )
})

const GameAudioOrVideo = observer(() => {
  const {
    game: {
      myPlayer,
      isBetModalOpen,
      isCallOptionsModalOpen,
      isGameStartSheetOpen,
      setPlayerSmallVideoPosition,
      previewVideoWasShown,
      seatedObserver,
      isMyPlayerObserver
    },
    communication: { isLocalUserJoined, isLocalUserVideoHidden, isLocalUserMuted }
  } = useStore()

  const { handleVideo } = useShortcuts()

  const amISeatedObserver = seatedObserver && seatedObserver?.id === myPlayer?.id
  const amIObserver = isMyPlayerObserver && !amISeatedObserver

  const showVideo = isLocalUserJoined && !isLocalUserVideoHidden && !amIObserver
  const showAudioIcon = isLocalUserJoined && isLocalUserVideoHidden && !isLocalUserMuted && !amIObserver
  const showMuted = isLocalUserJoined && isLocalUserVideoHidden && isLocalUserMuted && !amIObserver

  const viewRef = useRef<View>(null)

  if (showMuted) {
    return (
      <TouchableOpacity style={styles.audioIconContainer} onPress={handleVideo}>
        <MicIconOff />
      </TouchableOpacity>
    )
  }
  if (showAudioIcon) {
    return (
      <TouchableOpacity style={styles.audioIconContainer} onPress={handleVideo}>
        <DS.Icon name="Audio" size={24} color={'Global/Black'} />
      </TouchableOpacity>
    )
  }
  if (showVideo) {
    return (
      <View
        ref={viewRef}
        onLayout={(e) => {
          if (viewRef.current) {
            viewRef.current.measure((x, y, width, height, pageX, pageY) => {
              setPlayerSmallVideoPosition({ x: pageX, y: pageY })
            })
          }
        }}
        style={styles.video}
      >
        {previewVideoWasShown && !isBetModalOpen && !isCallOptionsModalOpen && !isGameStartSheetOpen ? (
          <TouchableOpacity style={styles.video} onPress={handleVideo}>
            <PlayerCellVideo videoStyle={{ borderRadius: 24, width: 48, height: 48 }} />
          </TouchableOpacity>
        ) : (
          <View style={styles.audioIconContainer}>
            <DS.Icon name="Camera" size={24} color={'Global/Black'} />
          </View>
        )}
      </View>
    )
  }

  return null
})

export const GameUnPausedPlayerOrTeacher = observer(() => {
  const router = useRouter()
  const {
    game: {
      isMyTurn,
      myPlayer,
      isGameOver,
      isTournamentGameOver,
      hasUserTappedOnAction,
      isGamePaused,
      showPlayerTimerAfterJoinedGame,
      showImBack,
      shouldShowPreFold,
      isMyPlayerObserver,
      isMyPlayerFoldedOrWaiting,
      playerActionTimerExpired,
      isWalkthrough,
      isMyPlayerOut,
      isHandWinnersEvent,
      isPracticeMode,
      isPracticeTournamentMode,
      isThreeCard,
      isRaiseOrFold,
      isMyPlayerWaiting,
      hasUserDeclinedToBuy,
      isTournamentModeOn,
      bots,
      gameKind,
      players,
      isMyPlayerTeacher
    },
    network: { isWebSocketConnected },
    analytics
  } = useStore()

  const { handlePlayerAction: onPlayerAction } = usePlayerActions()
  const { handleOpenChats: onChat } = useGameBottomModal()

  const botsOutChips = bots.length > 0 && bots.every((bot) => bot.chips === 0 && bot.buyIn && bot.isOut())

  const showPreFold = shouldShowPreFold()
  const gameEndedForMyPlayer =
    isGameOver || (isMyPlayerOut && hasUserDeclinedToBuy) || (isMyPlayerOut && isTournamentModeOn)

  const disablePlayerActions =
    hasUserTappedOnAction ||
    !isMyTurn ||
    isGamePaused ||
    !showPlayerTimerAfterJoinedGame ||
    !isWebSocketConnected ||
    playerActionTimerExpired

  const showTimer =
    isHandWinnersEvent && !isPracticeMode && !isPracticeTournamentMode && !isRaiseOrFold && !isThreeCard && !isGameOver

  useEffect(() => {
    if (shouldDisplayStatisticsButton()) {
      analytics.setEvent(events.gameStats.game_stats_button_viewed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyPlayerObserver, myPlayer, isPracticeMode, botsOutChips, gameEndedForMyPlayer, isMyPlayerWaiting])

  const shouldDisplayStatisticsButton = () => {
    if (isMyPlayerObserver || myPlayer?.isSittingOut() || myPlayer?.isForeverOut()) {
      return false
    }

    if (isPracticeMode) {
      if (botsOutChips || gameEndedForMyPlayer) {
        return true
      }
    } else if (gameKind === GameModeTag.default) {
      const waitingPlayers = players.filter((x) => x.isWaiting())
      const playingPlayers = players.filter(
        (x) => !(x.isOut() || x.isForeverOut() || x.isSittingOut() || x.isDisconnected())
      )

      const myPlayerIsWaitingAlone =
        waitingPlayers.length === 1 && playingPlayers.length <= 1 ? waitingPlayers[0].id === myPlayer?.id : false

      if (gameEndedForMyPlayer || myPlayerIsWaitingAlone) {
        return true
      }
    } else {
      if (isMyPlayerWaiting) {
        return false
      }

      if (gameEndedForMyPlayer) {
        return true
      }
    }
    return false
  }

  const onPressGameStats = () => {
    analytics.setEvent(events.gameStats.game_stats_button_tapped, {
      game_state: isGameOver ? 'post game' : 'during game'
    })
    router.navigate('/game/statistics')
  }

  const shouldDisplayChatButton =
    (isMyPlayerObserver || isMyPlayerFoldedOrWaiting) && !isWalkthrough && !(isMyPlayerObserver && isMyPlayerTeacher)

  if (!myPlayer) return null

  return (
    <>
      {shouldDisplayStatisticsButton() && (
        <GameActions
          leftIcon={<Image style={{ width: 30, height: 30 }} source={ColoredStars} />}
          rightIcon={<DS.Icon name={'Arrow---right'} size={20} color="Global/Black" />}
          leftIconBg={'Global/White'}
          title={'Your game stats'}
          onSelectingOption={onPressGameStats}
        />
      )}

      <View style={[styles.secondRow, { flexDirection: shouldDisplayChatButton ? 'column-reverse' : 'column' }]}>
        {!shouldDisplayStatisticsButton() && shouldDisplayChatButton ? (
          <DS.Button
            variant="Large/Primary"
            type="On Light"
            leftIcon={<ChatIcon />}
            onPress={() => {
              if (!onChat) return
              onChat()
              analytics.setEvent(events.game.start_chatting_tapped)
            }}
            centerContent
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              width: '99%'
            }}
          >
            {translate('newGameScreen.common.startChatting')}
          </DS.Button>
        ) : (
          <PlayerActions
            actions={myPlayer?.actions}
            onPlayerAction={disablePlayerActions ? undefined : onPlayerAction}
            imBack={showImBack}
            showPreFold={showPreFold}
          />
        )}

        <View style={[styles.playerLastActionContainer, { paddingTop: 16 }, !isMyTurn ? { alignItems: 'center' } : {}]}>
          <GameAudioOrVideo />

          {showTimer ? (
            <TimerShowCards
              showNextHandIn={!isTournamentGameOver}
              durationSeconds={GAME_CONFIG.DELAY_BETWEEN_HANDS + 1}
            />
          ) : (
            <DS.Frame flex={1}>
              <PlayerHandLastAction hiddenProgressBar={disablePlayerActions} />
            </DS.Frame>
          )}
        </View>
      </View>
    </>
  )
})

const GameUnPausedObserver = observer(() => {
  const router = useRouter()
  const {
    game: { leaveCurrentGame }
  } = useStore()

  const onFindGamePress = async () => {
    await leaveCurrentGame()
    router.replace('/game/join')
  }
  return (
    <View style={[styles.secondRow, { flexDirection: 'column' }]}>
      <DS.Button
        variant="Large/Primary"
        type="On Light"
        leftIcon={<ChatIcon />}
        onPress={onFindGamePress}
        centerContent
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          width: '99%'
        }}
      >
        Find a game
      </DS.Button>
      <View style={[styles.playerLastActionContainer, { alignItems: 'center' }]}>
        <DS.Frame flex={1}>
          <PlayerHandLastAction hiddenProgressBar={true} />
        </DS.Frame>
      </View>
    </View>
  )
})

export const PlayerHand = observer(() => {
  const {
    game: { myPlayer, isGameOver, isGamePaused, isGamePlaying, isMyPlayerObserver, isMyPlayerTeacher, walkthroughType }
  } = useStore()

  const { handlePlayerAction: onPlayerAction } = usePlayerActions()

  const HEIGHT = useActualHeight()
  const isSmallerDevice = HEIGHT < 670
  const gap = isSmallerDevice ? -10 : 0

  if (walkthroughType && ['RaiseOrFold', 'DealTheFlop'].includes(walkthroughType)) {
    return <PlayerHandWalkthrough onPlayerAction={onPlayerAction} gap={gap} />
  }

  const height = useActualHeight()
  const isMediumDevice = height < 900 && height > 670
  const isBigDevice = height >= 900

  const showGameUnPausedForPlayerOrTeacher =
    !isGamePaused && (!isMyPlayerObserver || (isMyPlayerTeacher && isMyPlayerObserver))
  const showGameUnPauseForObserver = !(isGamePaused || isMyPlayerTeacher) && isGamePlaying && isMyPlayerObserver

  return (
    <DS.Frame style={[styles.container, { gap: DS.spacing[16] }]} id="player-hand">
      {myPlayer ? (
        <>
          {/**CHeck this is not showing on this condition  (isMediumDevice || isBigDevice)*/}

          {(!isMyPlayerTeacher || (!isMyPlayerObserver && isMyPlayerTeacher)) && (
            <View style={styles.firstRow}>
              <PlayerInfo />
            </View>
          )}

          {isGamePaused && !isMyPlayerTeacher && <GamePaused />}
          {showGameUnPausedForPlayerOrTeacher && <GameUnPausedPlayerOrTeacher />}
          {showGameUnPauseForObserver && <GameUnPausedObserver />}
        </>
      ) : isGameOver ? (
        <View style={styles.noPlayerInfo}>
          <PlayerHandLastAction hiddenProgressBar={true} />
        </View>
      ) : (
        <DS.Frame gap="8" justifyContent="center" alignItems="stretch">
          <DS.Frame row justifyContent="space-between" alignItems="center">
            <SkeletonLoader height={20} width={100} />
            <SkeletonLoader height={20} width={100} />
          </DS.Frame>
          <SkeletonLoader height={40} />
          <SkeletonLoader height={30} />
        </DS.Frame>
      )}
    </DS.Frame>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: DS.spacing[16],
    paddingTop: DS.spacing[20],
    gap: DS.spacing[8]
  },
  firstRow: {
    flexDirection: 'row',
    width: '100%'
  },
  secondRow: {
    // gap: 16,
    justifyContent: 'center',
    alignItems: 'center'
  },
  playerLastActionContainer: {
    width: '100%',
    flexDirection: 'row',
    gap: DS.spacing[16],
    alignItems: 'center'
  },
  video: {
    width: 48,
    height: 48,
    borderRadius: 24
  },
  audioIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  initials: {
    fontFamily: fontFamily.FeatureDeckMedium,
    fontSize: 14,
    color: color.palette.white
  },
  noPlayerInfo: {
    justifyContent: 'center'
  }
})
